import React, { useState } from 'react';
import { useTeamChat } from '@/context/TeamChatContext';
import { useWorkspace } from '@/context/WorkspaceContext';
import { cn } from '@/lib/utils';
import { Lock, ChevronDown, ChevronRight, Plus } from 'lucide-react';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import CreateGroupDialog from './CreateGroupDialog';
import NewMessageDialog from './NewMessageDialog';

interface GroupSidebarProps {
  isGlobalDarkMode: boolean;
  onChatSelect?: () => void;
  hideHeader?: boolean;
}

const GroupSidebar: React.FC<GroupSidebarProps> = ({ isGlobalDarkMode, onChatSelect, hideHeader = false }) => {
  const {
    channels,
    directMessages,
    activeChannelId,
    activeDmId,
    setActiveChannel,
    setActiveDm,
    getDmParticipant,
    currentUser,
    joinChannel,
    onlineUsers
  } = useTeamChat();

  const { userRole } = useWorkspace();

  const [channelsOpen, setChannelsOpen] = useState(true);
  const [dmsOpen, setDmsOpen] = useState(true);
  const [createGroupOpen, setCreateGroupOpen] = useState(false);
  const [newMessageOpen, setNewMessageOpen] = useState(false);

  const textColor = isGlobalDarkMode ? 'text-sidebar-foreground' : 'text-gray-900';
  const subTextColor = isGlobalDarkMode ? 'text-sidebar-foreground/60' : 'text-gray-600';
  const sidebarBg = isGlobalDarkMode ? 'bg-sidebar' : 'bg-sidebar-light-secondary';

  return (
    <div className={cn("h-full w-full flex flex-col lg:border-r", sidebarBg, isGlobalDarkMode ? "border-sidebar-border" : "border-gray-200")}>
      {/* Scrollable Content */}
      <div className={cn("flex-1 overflow-y-auto", sidebarBg)}>
        <nav className="p-3">
          {/* Groups Section */}
          <Collapsible open={channelsOpen} onOpenChange={setChannelsOpen} className="mb-6">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-1">
                <CollapsibleTrigger asChild>
                  <button className={cn("p-1 rounded transition-colors", isGlobalDarkMode ? "hover:bg-sidebar-accent text-sidebar-foreground/60" : "hover:bg-sidebar-light-hover text-gray-500")}>
                    {channelsOpen ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
                  </button>
                </CollapsibleTrigger>
                <h3 className={cn("text-xs font-semibold uppercase tracking-wider", subTextColor)}>
                  Group Chats
                </h3>
              </div>

              {/* Only Admin/Manager/Owner can create groups */}
              {['owner', 'admin', 'manager'].includes(userRole || '') && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setCreateGroupOpen(true);
                  }}
                  className={cn("p-1 rounded transition-colors", isGlobalDarkMode ? "hover:bg-sidebar-accent text-sidebar-foreground/60" : "hover:bg-sidebar-light-hover text-gray-500")}
                  title="Create Group"
                >
                  <Plus className="w-3.5 h-3.5" />
                </button>
              )}
            </div>

            <CollapsibleContent>
              <ul className="space-y-1">
                {channels
                  .filter(c => currentUser && c.members.includes(currentUser.id))
                  .map(channel => (
                    <li key={channel.id}>
                      <button
                        onClick={() => {
                          setActiveChannel(channel.id);
                          onChatSelect?.();
                        }}
                        className={cn(
                          "w-full px-3 py-3 lg:py-2.5 flex items-center gap-3 rounded-lg text-sm font-medium transition-all duration-200 text-left active:scale-[0.98] border",
                          activeChannelId === channel.id
                            ? isGlobalDarkMode ? "bg-nav-active text-white shadow-md font-bold border-transparent" : "bg-sidebar-light-active text-gray-900 font-bold shadow-sm border-gray-200"
                            : (isGlobalDarkMode ? "text-sidebar-foreground/80 lg:hover:text-sidebar-foreground lg:hover:bg-sidebar-accent border-transparent" : "text-gray-700 lg:hover:text-gray-900 hover:bg-sidebar-light-hover border-transparent")
                        )}
                      >
                        <div className="relative">
                          <Avatar className="w-10 h-10 flex-shrink-0">
                            <AvatarImage src={channel.avatar} alt={channel.name} />
                            <AvatarFallback className={cn("text-xs text-white", channel.avatarColor || "bg-slate-500")}>
                              {channel.name.charAt(0).toUpperCase()}
                            </AvatarFallback>
                          </Avatar>
                          <span className={cn(
                            "absolute bottom-0 right-0 w-3 h-3 rounded-full border-2 transition-colors",
                            activeChannelId === channel.id ? (isGlobalDarkMode ? "border-nav-active" : "border-sidebar-light-active shadow-sm") : (isGlobalDarkMode ? "border-sidebar" : "border-white"),
                            channel.members.some(m => m !== currentUser?.id && onlineUsers.includes(m)) ? 'bg-green-500' : 'bg-gray-400'
                          )} />
                        </div>
                        <div className="flex flex-col flex-1 min-w-0">
                          <div className="flex items-center justify-between gap-1">
                            <div className="flex items-center gap-1 min-w-0">
                              <span className="truncate">{channel.name}</span>
                              {channel.isPrivate && (
                                <Lock className={cn("w-3.5 h-3.5 flex-shrink-0 opacity-50", activeChannelId === channel.id && (isGlobalDarkMode ? "text-white opacity-100" : "text-gray-900 opacity-100"))} />
                              )}
                            </div>
                            {channel.unreadCount > 0 && (
                              <span className={cn(
                                "text-[10px] px-1.5 py-0.5 rounded-full flex-shrink-0 font-bold",
                                activeChannelId === channel.id ? "bg-white text-nav-active shadow-sm" : "bg-nav-active text-white"
                              )}>
                                {channel.unreadCount}
                              </span>
                            )}
                          </div>
                          {channel.lastMessage && (
                            <p className={cn(
                              "text-[11px] truncate opacity-60 mt-0.5 font-normal",
                              activeChannelId === channel.id ? (isGlobalDarkMode ? "text-white/80" : subTextColor) : subTextColor
                            )}>
                              {channel.lastMessage}
                            </p>
                          )}
                        </div>
                      </button>
                    </li>
                  ))}
              </ul>

              {/* Discoverable Groups Section - Nested */}
              {channels.some(c => currentUser && !c.members.includes(currentUser.id) && !c.isPrivate) && (
                <div className={cn("mt-4 pt-2 border-t", isGlobalDarkMode ? "border-sidebar-border" : "border-gray-200/50")}>
                  <div className="flex items-center gap-1 mb-2 px-1">
                    <h3 className={cn("text-xs font-semibold uppercase tracking-wider", subTextColor)}>
                      Discover Groups
                    </h3>
                  </div>
                  <ul className="space-y-1">
                    {channels
                      .filter(c => currentUser && !c.members.includes(currentUser.id) && !c.isPrivate)
                      .map(channel => (
                        <li key={channel.id}>
                          <button
                            onClick={() => {
                              setActiveChannel(channel.id);
                              onChatSelect?.();
                            }}
                            className={cn(
                              "w-full px-3 py-2.5 flex items-center gap-3 rounded-lg text-sm font-medium transition-all duration-200 text-left group active:scale-[0.98] border",
                              activeChannelId === channel.id
                                ? isGlobalDarkMode ? "bg-nav-active text-white shadow-md border-transparent" : "bg-sidebar-light-active text-gray-900 shadow-sm border-gray-200"
                                : (isGlobalDarkMode ? "text-sidebar-foreground/80 hover:bg-sidebar-accent border-transparent" : "text-gray-700 hover:bg-sidebar-light-hover border-transparent")
                            )}
                          >
                            <Avatar className="w-10 h-10 flex-shrink-0">
                              <AvatarImage src={channel.avatar} alt={channel.name} />
                              <AvatarFallback className={cn("text-xs text-white", channel.avatarColor || "bg-slate-500")}>
                                {channel.name.charAt(0).toUpperCase()}
                              </AvatarFallback>
                            </Avatar>
                            <div className="flex items-center gap-1 flex-1 min-w-0">
                              <span className="truncate">{channel.name}</span>
                            </div>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                joinChannel(channel.id);
                              }}
                              className={cn(
                                "text-[10px] px-2 py-0.5 rounded border transition-colors opacity-0 group-hover:opacity-100",
                                isGlobalDarkMode
                                  ? "border-sidebar-border hover:bg-sidebar-accent text-sidebar-foreground/80"
                                  : "border-gray-300 hover:bg-sidebar-light-hover text-gray-600"
                              )}
                            >
                              Join
                            </button>
                          </button>
                        </li>
                      ))}
                  </ul>
                </div>
              )}
            </CollapsibleContent>
          </Collapsible>

          {/* Direct Messages Section */}
          <Collapsible open={dmsOpen} onOpenChange={setDmsOpen}>
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-1">
                <CollapsibleTrigger asChild>
                  <button className={cn("p-1 rounded transition-colors", isGlobalDarkMode ? "hover:bg-sidebar-accent text-sidebar-foreground/60" : "hover:bg-sidebar-light-hover text-gray-500")}>
                    {dmsOpen ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
                  </button>
                </CollapsibleTrigger>
                <h3 className={cn("text-xs font-semibold uppercase tracking-wider", subTextColor)}>
                  Direct messages
                </h3>
              </div>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setNewMessageOpen(true);
                }}
                className={cn("p-1 rounded transition-colors", isGlobalDarkMode ? "hover:bg-sidebar-accent text-sidebar-foreground/60" : "hover:bg-sidebar-light-hover text-gray-500")}
                title="New Message"
              >
                <Plus className="w-3.5 h-3.5" />
              </button>
            </div>

            <CollapsibleContent>
              <ul className="space-y-1">
                {directMessages
                  .filter(dm => dm.lastMessage || dm.id === activeDmId)
                  .map(dm => {
                  const participant = getDmParticipant(dm);
                  if (!participant) return null;

                  return (
                    <li key={dm.id}>
                      <button
                        onClick={() => {
                          setActiveDm(dm.id);
                          onChatSelect?.();
                        }}
                        className={cn(
                          "w-full px-3 py-3 lg:py-2.5 flex items-center gap-3 rounded-lg text-sm font-medium transition-all duration-200 text-left active:scale-[0.98] border",
                          activeDmId === dm.id
                            ? isGlobalDarkMode ? "bg-nav-active text-white shadow-md font-bold border-transparent" : "bg-sidebar-light-active text-gray-900 font-bold shadow-sm border-gray-200"
                            : (isGlobalDarkMode ? "text-sidebar-foreground/80 lg:hover:text-sidebar-foreground lg:hover:bg-sidebar-accent border-transparent" : "text-gray-700 lg:hover:text-gray-900 hover:bg-sidebar-light-hover border-transparent")
                        )}
                      >
                        <div className="relative">
                          <Avatar className="w-10 h-10">
                            <AvatarImage src={participant.avatar} />
                            <AvatarFallback className={cn("text-xs transition-colors", activeDmId === dm.id ? "bg-white/20 text-white" : (isGlobalDarkMode ? "bg-sidebar-accent text-sidebar-foreground/80" : "bg-slate-200 text-slate-700"))}>
                              {participant.displayName.charAt(0)}
                            </AvatarFallback>
                          </Avatar>
                          <span className={cn(
                            "absolute bottom-0 right-0 w-3 h-3 rounded-full border-2 transition-colors",
                            activeDmId === dm.id ? (isGlobalDarkMode ? "border-nav-active" : "border-sidebar-light-active shadow-sm") : (isGlobalDarkMode ? "border-sidebar" : "border-white"),
                            onlineUsers.includes(participant.id) ? 'bg-green-500' : 'bg-gray-400'
                          )} />
                        </div>
                        <div className="flex flex-col flex-1 min-w-0">
                          <div className="flex items-center justify-between gap-1">
                            <span className="truncate">{participant.displayName}</span>
                            {dm.unreadCount > 0 && (
                              <span className={cn(
                                "text-[10px] px-1.5 py-0.5 rounded-full flex-shrink-0 font-bold",
                                activeDmId === dm.id ? "bg-white text-nav-active shadow-sm" : "bg-nav-active text-white"
                              )}>
                                {dm.unreadCount}
                              </span>
                            )}
                          </div>
                          {dm.lastMessage && (
                            <p className={cn(
                              "text-[11px] truncate opacity-60 mt-0.5 font-normal",
                              activeDmId === dm.id ? (isGlobalDarkMode ? "text-white/80" : subTextColor) : subTextColor
                            )}>
                              {dm.lastMessage}
                            </p>
                          )}
                        </div>
                      </button>
                    </li>
                  );
                })}
              </ul>
            </CollapsibleContent>
          </Collapsible>
        </nav>
      </div>

      <CreateGroupDialog open={createGroupOpen} onOpenChange={setCreateGroupOpen} isGlobalDarkMode={isGlobalDarkMode} />
      <NewMessageDialog open={newMessageOpen} onOpenChange={setNewMessageOpen} isGlobalDarkMode={isGlobalDarkMode} />
    </div>
  );
};

export default GroupSidebar;
